import {
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useState, useMemo, useContext, useEffect } from 'react';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import WomanOutlinedIcon from '@mui/icons-material/WomanOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext, AppContext } from 'src/store/ContextStore';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import axios from 'src/axios';
import CanRiskModal from './export-menu/CanRiskModal';
import { downloadCanRisk } from './export-menu/canriskDownload';
import RiskRecordsList from './RiskRecordsList';
import GailScoreModal from './GailScoreModal';
import Qrisk3Modal from './qrisk3/Qrisk3Modal';
import { MODEL_NAME as QRISK3_MODEL_NAME } from './qrisk3/qrisk3Text';
import RiskToolTiles from './RiskToolTiles';
import TriagePanel from './triage/TriagePanel';
import { TriagePeopleContext } from './triage/triagePeople';
import {
  buildTriageReportHtml,
  saveTriageReportPdf
} from './triage/triageReport';
import { SIDEBAR_VALUE } from '../sidebarValues';
import { useAutoTranslation, useLanguage } from 'src/hooks/useTranslate';
import useDateFormatter from 'src/hooks/useDateFormatter';

// A heading over a part of the section, as the triage panel's is.
const PART_LABEL_SX = {
  fontSize: '.75rem',
  fontWeight: 700,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: 0.75
};

function RiskFactorsMenu({
  data,
  modificationHistoryHandle,
  reportSummary,
  // TGCLOUD-1910: the triage panel names relatives from the loaded pedigree
  // (never on an anonymous deployment: `anonymous` is the fail-closed flag)
  // and selects them on the chart.
  memberRecords,
  anonymous = true,
  onSelectMember
}) {
  const t = useAutoTranslation();
  const lang = useLanguage();
  const fmtDate = useDateFormatter();

  const { setSnack } = useContext(SnackContext);
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0);
  const [gailModalOpen, setGailModalOpen] = useState(false);
  const [canRiskModalOpen, setCanRiskModalOpen] = useState(false);

  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  // Triage rule packs (TGCLOUD-1910), two-tier like every group feature. The
  // server also answers TRIAGE_OFF for a family whose group has it off.
  const triageOn =
    content?.settings?.systemFeatures?.FEATURE_TRIAGE_RULE_PACKS === true;
  // QRISK3 cardiovascular risk (TGCLOUD-1911), two-tier the same way; the
  // server answers QRISK3_OFF for a family whose group has it off.
  const qrisk3On = content?.settings?.systemFeatures?.FEATURE_QRISK3 === true;
  const [qrisk3ModalOpen, setQrisk3ModalOpen] = useState(false);
  const [triageEvaluation, setTriageEvaluation] = useState(null);
  const [savingPdf, setSavingPdf] = useState(false);
  const triagePeople = useMemo(
    () => ({ memberRecords, anonymous, onSelectMember }),
    [memberRecords, anonymous, onSelectMember]
  );

  const user_id = useMemo(() => user?.user_id || user?._id, [user]);
  const familyId = user?.familyId || user_id;
  const gender = useMemo(() => data?.gender, [data]);
  // Canonical member key — scopes the score cards to the selected member.
  const memberId = useMemo(
    () => data?.proBandId ?? data?.familyMemberId,
    [data?.proBandId, data?.familyMemberId]
  );

  // TGCLOUD-1669 Phase 1 — the collapsed-row summary.
  //
  // This component owns no records: two or three RiskRecordsList children each
  // fetch and filter their own. So the facts are collected from them and merged
  // here rather than refetched. The GAIL list only mounts for an assigned-female
  // record, so the set of reporters varies and we aggregate whoever mounted.
  const [riskFacts, setRiskFacts] = useState({});

  // FORGET THE PREVIOUS PATIENT. `riskFacts` only ever merged, and the Gail
  // list is mounted for a female only, so selecting a woman with N Gail scores
  // and then a man left `gail: { count: N }` in place with nothing to withdraw
  // it: the summary line reported one patient's score count under another
  // patient's name. That is the exact failure the three-valued summary contract
  // exists to prevent, so the facts are cleared the moment the member changes
  // and each list reports again from pending.
  useEffect(() => {
    setRiskFacts({});
  }, [memberId, gender]);
  const handleRiskFacts = React.useCallback((recordType, facts) => {
    setRiskFacts((prev) => {
      const before = prev[recordType];
      if (
        before &&
        before.status === facts.status &&
        before.count === facts.count
      ) {
        return prev;
      }
      return { ...prev, [recordType]: facts };
    });
  }, []);

  useEffect(() => {
    if (!reportSummary) return;
    const entries = Object.values(riskFacts);
    // Nothing has reported yet, or one list is still loading: say nothing
    // rather than reporting a total that is about to change.
    if (!entries.length || entries.some((e) => e.status === 'pending')) {
      reportSummary(SIDEBAR_VALUE.riskFactors, { status: 'pending' });
      return;
    }
    const count = entries.reduce((n, e) => n + e.count, 0);
    const leads = entries
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .map((e) => `${e.label} ${e.count}`);
    reportSummary(SIDEBAR_VALUE.riskFactors, { status: 'ready', count, leads });
  }, [reportSummary, riskFacts]);

  // The risk and triage summary: the latest risk records, read now, and the
  // triage evaluation the panel is showing.
  const handleSummaryPdf = () => {
    if (!user_id) return;
    setSavingPdf(true);
    axios
      .get(APICONSTANTS.getRiskRecords.replace(':user_id', user_id))
      .then(({ data: all }) => {
        const records = (Array.isArray(all) ? all : []).filter(
          (r) => String(r.familyMemberId) === String(memberId)
        );
        const row =
          (memberRecords || []).find((m) => m.pid === data?.pid) || data || {};
        const name = [row.firstName, row.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        const html = buildTriageReportHtml({
          person: { name, memberId, upn: row.upn },
          familyId: user?.familyId,
          records,
          evaluation: triageEvaluation,
          t,
          fmtDate,
          lang,
          memberRecords,
          anonymous: anonymous || row.isHide === true
        });
        return saveTriageReportPdf(html, `risk-and-triage-${memberId}.pdf`);
      })
      .catch(() =>
        setSnack({
          status: true,
          msg: t('The summary could not be produced.'),
          severity: 'error'
        })
      )
      .finally(() => setSavingPdf(false));
  };

  // The risk tools, as tiles (Tim, QA 2026-10-16). A model's own name is shown
  // as it stands; what it calculates is translated.
  const tools = [
    {
      key: 'canrisk',
      label: 'CanRisk', // i18n-ignore: the model's own name
      caption: t('Cancer risk, BOADICEA'),
      Icon: ScienceOutlinedIcon,
      onClick: () => setCanRiskModalOpen(true),
      vaAnchor: 'pedigree.risk.calculateCanRisk'
    },
    gender === 'female' && {
      key: 'gail',
      label: 'Gail', // i18n-ignore: the model's own name
      caption: t('5-year breast cancer risk'),
      Icon: WomanOutlinedIcon,
      onClick: () => setGailModalOpen(true),
      vaAnchor: 'pedigree.risk.calculateGail'
    },
    qrisk3On && {
      key: 'qrisk3',
      label: t('Cardiovascular'),
      caption: t('10-year risk, {{model}}', { model: QRISK3_MODEL_NAME }),
      Icon: FavoriteBorderOutlinedIcon,
      onClick: () => setQrisk3ModalOpen(true),
      disabled: !user_id,
      disabledReason: user_id ? '' : t('Please save the pedigree first')
    }
  ].filter(Boolean);

  const handleCanRiskDownload = (allOptionsArray, targetId) =>
    downloadCanRisk({
      userId: user_id,
      familyId,
      members: allOptionsArray,
      targetId,
      setSnack
    });

  return (
    <div style={{ maxWidth: 390 }}>
      <Grid container spacing={2}>
        <Grid size={12} sx={{ mt: 1 }}>
          <Typography sx={PART_LABEL_SX}>{t('Risk tools')}</Typography>
          <RiskToolTiles tools={tools} />
        </Grid>

        {gender === 'female' && (
          <Grid size={12}>
            <RiskRecordsList
              onFacts={handleRiskFacts}
              userId={user_id}
              refreshKey={recordsRefreshKey}
              recordType="gail"
              memberIds={[memberId]}
              scoredOnly
            />
          </Grid>
        )}
        <Grid size={12}>
          <RiskRecordsList
            onFacts={handleRiskFacts}
            userId={user_id}
            refreshKey={recordsRefreshKey}
            recordType="canrisk"
            memberIds={[memberId]}
            scoredOnly
          />
        </Grid>
        {qrisk3On && (
          <>
            <Grid size={12}>
              <RiskRecordsList
                onFacts={handleRiskFacts}
                userId={user_id}
                refreshKey={recordsRefreshKey}
                recordType="qrisk3"
                memberIds={[memberId]}
                scoredOnly
              />
            </Grid>
            <Grid size={12}>
              <RiskRecordsList
                onFacts={handleRiskFacts}
                userId={user_id}
                refreshKey={recordsRefreshKey}
                recordType="measurements"
                memberIds={[memberId]}
                scoredOnly
              />
            </Grid>
          </>
        )}
        {/* PRS is stored, not calculated in-app (no genotype/VCF data) — a
            score is entered via the "Risk Factor" clinical record (PRS radio
            option) and simply surfaces here as a peer to Gail/CanRisk. */}
        <Grid size={12}>
          <RiskRecordsList
            onFacts={handleRiskFacts}
            userId={user_id}
            refreshKey={recordsRefreshKey}
            recordType="prs"
            memberIds={[memberId]}
            scoredOnly
          />
        </Grid>
        {triageOn && (
          <TriagePeopleContext.Provider value={triagePeople}>
            <Grid size={12}>
              <TriagePanel
                userId={user_id}
                familyMemberId={memberId}
                refreshKey={recordsRefreshKey}
                onRecorded={() => setRecordsRefreshKey((k) => k + 1)}
                onEvaluation={setTriageEvaluation}
              />
            </Grid>
            <Grid size={12}>
              <RiskRecordsList
                onFacts={handleRiskFacts}
                userId={user_id}
                refreshKey={recordsRefreshKey}
                recordType="triage"
                memberIds={[memberId]}
                scoredOnly
              />
            </Grid>
          </TriagePeopleContext.Provider>
        )}
        {/* Not sex-gated: CanRisk covers prostate and pancreatic cancer, and PRS
            is trait-agnostic, so male individuals have risk records too — gating
            this on female left their audit history unreachable. The Gail list
            above stays female-gated because the Gail model itself is. */}
        <Grid
          size={12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}
        >
          {/* The risk and triage summary, for a record outside TrakGene (an EMR): an icon beside
              the history icon, where a full-width button had stood (Tim, QA 2026-10-16). */}
          {triageOn && (
            <Tooltip title={t('Download risk and triage summary (PDF)')}>
              <span>
                <IconButton
                  aria-label={t('Download risk and triage summary (PDF)')}
                  onClick={handleSummaryPdf}
                  disabled={savingPdf || !user_id}
                  data-testid="risk-summary-pdf"
                >
                  {savingPdf ? (
                    <CircularProgress size={20} />
                  ) : (
                    <FileDownloadOutlinedIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            onClick={() =>
              modificationHistoryHandle(CONSTANTS.auditLogType['riskFactors'])
            }
          >
            <HistoryIcon />
          </IconButton>
        </Grid>
      </Grid>
      {/* Calculate Gail Score - fetches saved inputs on open (no form) */}
      <GailScoreModal
        open={gailModalOpen}
        onClose={() => setGailModalOpen(false)}
        data={data}
        familyId={familyId}
        onCalculated={() => setRecordsRefreshKey((k) => k + 1)}
      />
      {qrisk3On && (
        <Qrisk3Modal
          open={qrisk3ModalOpen}
          onClose={() => setQrisk3ModalOpen(false)}
          userId={user_id}
          familyMemberId={memberId}
          memberRecords={memberRecords}
          anonymous={anonymous}
          onCalculated={() => setRecordsRefreshKey((k) => k + 1)}
          // A draft's readings are recorded as clinical measurements.
          onSaved={() => setRecordsRefreshKey((k) => k + 1)}
        />
      )}
      <CanRiskModal
        data={data}
        open={canRiskModalOpen}
        onClose={(hadResult) => {
          setCanRiskModalOpen(false);
          if (hadResult) setRecordsRefreshKey((k) => k + 1);
        }}
        onDownload={handleCanRiskDownload}
        userId={user_id}
      />
    </div>
  );
}

export default RiskFactorsMenu;
